import { ApolloClient } from "@apollo/client";
import {
  IAdjustmentTypeRepositoryResponse,
  IAdjustmentCategoriesRepositoryResponse,
  IDeductionTypeCategoriesRepositoryResponse,
  IAccountingSystemRepositoryResponse,
  IBrandMemberRepositoryResponse,
  IDeductionDocumentSubTypeRepositoryResponse,
  IDeductionTypeRepositoryResponse,
  IDistributionCenterRepositoryResponse,
  IDocumentTypeRepositoryResponse,
  IGLCodeRepositoryResponse,
  ILookupRepository,
  IPriorityRepositoryResponse,
  IPurchaserRepositoryResponse,
  IRetailerRepositoryResponse,
  IStageRepositoryResponse,
  IValidityRepositoryResponse,
  IPromoTypeRepositoryResponse,
  IPromoStatusRepositoryResponse,
  IDisputeTrackingStatusRepositoryResponse,
  IBrandMembersRequest,
  IProductLookUpRequest,
  IProductLookUpRepositoryResponse,
} from "@/core/repositories/lookup";
import {
  AdjustmentType,
  AdjustmentTypeProps,
  Accounting,
  AccountingProps,
  AdjustmentCategories,
  AdjustmentCategoriesProps,
  DeductionTypeCategory,
  DeductionTypeCategoryProps,
  BrandMember,
  BrandMemberProps,
  DeductionDocumentSubType,
  DeductionDocumentSubTypeProps,
  DeductionType,
  DeductionTypeProps,
  DistributionCenter,
  DistributionCenterProps,
  DocumentTypeProps,
  GLCode,
  GLCodeProps,
  Priority,
  PriorityProps,
  Purchaser,
  PurchaserProps,
  Retailer,
  RetailerProps,
  Stage,
  StageProps,
  Validity,
  ValidityProps,
  ProductLookUp,
  ProductLookUpProps,
} from "@/core/entities/lookup";
import { PromoType, PromoTypeProps } from "@/core/entities/lookup/promoType";
import { PromoStatus, PromoStatusProps } from "@/core/entities/lookup/promoStatus";
import {
  DisputeTrackingStatus,
  DisputeTrackingStatusProps,
} from "@/core/entities/lookup/disputeTrackingStatus";
import { DocumentType } from "@/core/entities/lookup/documentType/DocumentType";
import {
  GET_ADJUSTMENT_TYPES,
  GET_ACCOUNTING_SYSTEMS,
  GET_ADJUSTMENT_CATEGORIES,
  GET_DEDUCTION_TYPE_CATEGORIES,
  GET_BRAND_MEMBERS,
  GET_DEDUCTION_TYPES,
  GET_DISTRIBUTION_CENTER,
  GET_DOCUMENT_SUB_TYPES,
  GET_DOCUMENT_TYPES,
  GET_GL_CODE,
  GET_PRIORITIES,
  GET_PURCHASER,
  GET_RETAILERS,
  GET_STAGES,
  GET_VALIDITIES,
  GET_PROMO_TYPES,
  GET_PROMO_STATUSES,
  GET_DISPUTE_TRACKING_STATUSES,
  GET_PRODUCTS,
} from "@/core/graphql/lookup";

// Document Types
interface IDocumentTypeQueryResponse {
  deductionDocumentTypes: {
    count: number;
    items: DocumentTypeProps[];
  };
}

// Brand Members
interface IBrandMemberQueryResponse {
  users: {
    items: BrandMemberProps[];
    count: number;
  };
}

// Priorities
interface IPriorityQueryResponse {
  priorities: {
    count: number;
    items: PriorityProps[];
  };
}

// Validities
interface IValidityQueryResponse {
  validities: {
    count: number;
    items: ValidityProps[];
  };
}

// Stages
interface IStageQueryResponse {
  stages: {
    count: number;
    items: StageProps[];
  };
}

// Deduction Types
interface IDeductionTypeQueryResponse {
  types: {
    count: number;
    items: DeductionTypeProps[];
  };
}

// GL Codes
interface IGLCodeQueryResponse {
  glCodes: {
    count: number;
    items: GLCodeProps[];
  };
}

// Distribution Centers
interface IDistributionCenterQueryResponse {
  distributionCenters: {
    count: number;
    items: DistributionCenterProps[];
  };
}

// Purchasers
interface IPurchaserQueryResponse {
  purchasers: {
    count: number;
    items: PurchaserProps[];
  };
}

// Retailers
interface IRetailerQueryResponse {
  retailers: {
    count: number;
    items: RetailerProps[];
  };
}

// Promo Types
interface IPromoTypeQueryResponse {
  promoTypes: {
    count: number;
    items: PromoTypeProps[];
  };
}

// Promo Statuses
interface IPromoStatusQueryResponse {
  promoStatuses: {
    count: number;
    items: PromoStatusProps[];
  };
}

// Dispute Tracking Statuses
interface IDisputeTrackingStatusQueryResponse {
  disputeTrackingStatuses: {
    count: number;
    items: DisputeTrackingStatusProps[];
  };
}

// Products
interface IProductLookUpQueryResponse {
  products: {
    items: ProductLookUpProps[];
    total: number;
    page: number;
    limit: number;
  };
}

// Adjustment Types
interface IAdjustmentTypeQueryResponse {
  adjustmentTypes: {
    count: number;
    items: AdjustmentTypeProps[];
  };
}

// Adjustment Categories
interface IAdjustmentCategoriesQueryResponse {
  adjustmentCategories: {
    count: number;
    items: AdjustmentCategoriesProps[];
  };
}

// Deduction Type Categories
interface IDeductionTypeCategoriesQueryResponse {
  deductionTypeCategories: {
    count: number;
    items: DeductionTypeCategoryProps[];
  };
}

// Deduction Document SubTypes
interface IDeductionDocumentSubTypeQueryResponse {
  deductionDocumentSubtypes: {
    count: number;
    items: DeductionDocumentSubTypeProps[];
  };
}

// Accounting Systems
interface IAccountingSystemQueryResponse {
  accountingSystems: {
    data: AccountingProps[];
  };
}

export class LookupRepository implements ILookupRepository {
  constructor(private readonly client: ApolloClient) {}

  // Document Types
  async getDocumentTypes(): Promise<IDocumentTypeRepositoryResponse> {
    const { data } = await this.client.query<IDocumentTypeQueryResponse>({
      query: GET_DOCUMENT_TYPES,
      variables: {
        params: {},
      },
    });

    if (!data) {
      throw new Error("Failed to fetch document types");
    }

    const documentTypesResponse = data.deductionDocumentTypes;

    return {
      documentTypes: documentTypesResponse.items.map(documentType => new DocumentType(documentType)),
      count: documentTypesResponse.count,
    };
  }

  // Brand Members
  async getBrandMembers(params: IBrandMembersRequest): Promise<IBrandMemberRepositoryResponse> {
    const { data } = await this.client.query<IBrandMemberQueryResponse>({
      query: GET_BRAND_MEMBERS,
      variables: {
        params: {
          ...params,
        },
      },
    });

    if (!data) {
      throw new Error("Failed to fetch brand members");
    }

    const brandMembersResponse = data;

    return {
      brandMembers: brandMembersResponse.users.items.map(brandMember => new BrandMember(brandMember)),
      count: brandMembersResponse.users.count,
    };
  }

  // Priorities
  async getPriorities(): Promise<IPriorityRepositoryResponse> {
    const { data } = await this.client.query<IPriorityQueryResponse>({
      query: GET_PRIORITIES,
      variables: {
        params: {},
      },
    });

    if (!data) {
      throw new Error("Failed to fetch priorities");
    }

    const prioritiesResponse = data.priorities;

    return {
      priorities: prioritiesResponse.items.map(priority => new Priority(priority)),
      count: prioritiesResponse.count,
    };
  }

  // Validities
  async getValidities(): Promise<IValidityRepositoryResponse> {
    const { data } = await this.client.query<IValidityQueryResponse>({
      query: GET_VALIDITIES,
      variables: {
        params: {},
      },
    });

    if (!data) {
      throw new Error("Failed to fetch validities");
    }

    const validitiesResponse = data.validities;

    return {
      validities: validitiesResponse.items.map(validity => new Validity(validity)),
      count: validitiesResponse.count,
    };
  }

  // Stages
  async getStages(): Promise<IStageRepositoryResponse> {
    const { data } = await this.client.query<IStageQueryResponse>({
      query: GET_STAGES,
      variables: {
        params: {},
      },
    });

    if (!data) {
      throw new Error("Failed to fetch stages");
    }

    const stagesResponse = data.stages;

    return {
      stages: stagesResponse.items.map(stage => new Stage(stage)),
      count: stagesResponse.count,
    };
  }

  // Deduction Types
  async getDeductionTypes(): Promise<IDeductionTypeRepositoryResponse> {
    const { data } = await this.client.query<IDeductionTypeQueryResponse>({
      query: GET_DEDUCTION_TYPES,
      variables: {
        params: {},
      },
    });

    if (!data) {
      throw new Error("Failed to fetch deduction types");
    }

    const deductionTypesResponse = data.types;

    return {
      deductionTypes: deductionTypesResponse.items.map(deductionType => new DeductionType(deductionType)),
      count: deductionTypesResponse.count,
    };
  }

  // GL Codes
  async getGLCodes(): Promise<IGLCodeRepositoryResponse> {
    const { data } = await this.client.query<IGLCodeQueryResponse>({
      query: GET_GL_CODE,
      variables: {
        params: {},
      },
    });

    if (!data) {
      throw new Error("Failed to fetch GL codes");
    }

    const glCodesResponse = data.glCodes;

    return {
      glCodes: glCodesResponse.items.map(glCode => new GLCode(glCode)),
      count: glCodesResponse.count,
    };
  }

  // Distribution Centers
  async getDistributionCenters(params?: { purchaserId?: number }): Promise<IDistributionCenterRepositoryResponse> {
    const { data } = await this.client.query<IDistributionCenterQueryResponse>({
      query: GET_DISTRIBUTION_CENTER,
      variables: {
        params: {
          purchaser_id: params?.purchaserId,
        },
      },
    });

    if (!data) {
      throw new Error("Failed to fetch distribution centers");
    }

    const distributionCentersResponse = data.distributionCenters;

    return {
      distributionCenters: distributionCentersResponse.items.map(
        distributionCenter => new DistributionCenter(distributionCenter)
      ),
      count: distributionCentersResponse.count,
    };
  }

  // Purchasers
  async getPurchasers(): Promise<IPurchaserRepositoryResponse> {
    const { data } = await this.client.query<IPurchaserQueryResponse>({
      query: GET_PURCHASER,
      variables: {
        params: {},
      },
    });

    if (!data) {
      throw new Error("Failed to fetch purchasers");
    }

    const purchasersResponse = data.purchasers;

    return {
      purchasers: purchasersResponse.items.map(purchaser => new Purchaser(purchaser)),
      count: purchasersResponse.count,
    };
  }

  // Retailers
  async getRetailers(): Promise<IRetailerRepositoryResponse> {
    const { data } = await this.client.query<IRetailerQueryResponse>({
      query: GET_RETAILERS,
    });

    if (!data) {
      throw new Error("Failed to fetch retailers");
    }

    const retailersResponse = data.retailers;

    return {
      retailers: retailersResponse.items.map(retailer => new Retailer(retailer)),
      count: retailersResponse.count,
    };
  }

  // Adjustment Types
  async getAdjustmentTypes(): Promise<IAdjustmentTypeRepositoryResponse> {
    const { data } = await this.client.query<IAdjustmentTypeQueryResponse>({
      query: GET_ADJUSTMENT_TYPES,
      variables: {
        params: {},
      },
    });

    if (!data) {
      throw new Error("Failed to fetch adjustment types");
    }

    const adjustmentTypesResponse = data.adjustmentTypes;

    return {
      adjustmentTypes: adjustmentTypesResponse.items.map(adjustmentType => new AdjustmentType(adjustmentType)),
      count: adjustmentTypesResponse.count,
    };
  }

  // Adjustment Categories
  async getAdjustmentCategories(): Promise<IAdjustmentCategoriesRepositoryResponse> {
    const { data } = await this.client.query<IAdjustmentCategoriesQueryResponse>({
      query: GET_ADJUSTMENT_CATEGORIES,
      variables: {
        params: {},
      },
    });

    if (!data) {
      throw new Error("Failed to fetch adjustment categories");
    }

    const adjustmentCategoriesResponse = data.adjustmentCategories;

    return {
      adjustmentCategories: adjustmentCategoriesResponse.items.map(
        adjustmentCategory => new AdjustmentCategories(adjustmentCategory)
      ),
      count: adjustmentCategoriesResponse.count,
    };
  }

  // Deduction Type Categories
  async getDeductionTypeCategories(): Promise<IDeductionTypeCategoriesRepositoryResponse> {
    const { data } = await this.client.query<IDeductionTypeCategoriesQueryResponse>({
      query: GET_DEDUCTION_TYPE_CATEGORIES,
      variables: {
        params: {},
      },
    });

    if (!data) {
      throw new Error("Failed to fetch deduction type categories");
    }

    const deductionTypeCategoriesResponse = data.deductionTypeCategories;

    return {
      deductionTypeCategories: deductionTypeCategoriesResponse.items.map(
        deductionTypeCategory => new DeductionTypeCategory(deductionTypeCategory)
      ),
      count: deductionTypeCategoriesResponse.count,
    };
  }

  // Deduction Document SubTypes
  async getDocumentSubTypes(): Promise<IDeductionDocumentSubTypeRepositoryResponse> {
    const { data } = await this.client.query<IDeductionDocumentSubTypeQueryResponse>({
      query: GET_DOCUMENT_SUB_TYPES,
      variables: {
        params: {},
      },
    });

    if (!data) {
      throw new Error("Failed to fetch document sub types");
    }

    const documentSubTypesResponse = data.deductionDocumentSubtypes;

    return {
      documentSubTypes: documentSubTypesResponse.items.map(
        documentSubType => new DeductionDocumentSubType(documentSubType)
      ),
      count: documentSubTypesResponse.count,
    };
  }

  // Accounting Systems
  async getAccountingSystems(): Promise<IAccountingSystemRepositoryResponse> {
    const { data } = await this.client.query<IAccountingSystemQueryResponse>({
      query: GET_ACCOUNTING_SYSTEMS,
    });

    if (!data) {
      throw new Error("Failed to fetch accounting systems");
    }

    const accountingSystemsResponse = data.accountingSystems.data;

    return {
      accountingSystems: accountingSystemsResponse.map(accountingSystem => new Accounting(accountingSystem)),
      count: accountingSystemsResponse.length,
    };
  }

  // Promo Types
  async getPromoTypes(): Promise<IPromoTypeRepositoryResponse> {
    const { data } = await this.client.query<IPromoTypeQueryResponse>({
      query: GET_PROMO_TYPES,
    });

    if (!data) {
      throw new Error("Failed to fetch promo types");
    }

    const promoTypesResponse = data.promoTypes;

    return {
      promoTypes: promoTypesResponse.items.map(promoType => new PromoType(promoType)),
      count: promoTypesResponse.count,
    };
  }

  // Promo Statuses
  async getPromoStatuses(): Promise<IPromoStatusRepositoryResponse> {
    const { data } = await this.client.query<IPromoStatusQueryResponse>({
      query: GET_PROMO_STATUSES,
    });

    if (!data) {
      throw new Error("Failed to fetch promo statuses");
    }

    const promoStatusesResponse = data.promoStatuses;

    return {
      promoStatuses: promoStatusesResponse.items.map(promoStatus => new PromoStatus(promoStatus)),
      count: promoStatusesResponse.count,
    };
  }

  // Dispute Tracking Statuses
  async getDisputeTrackingStatuses(): Promise<IDisputeTrackingStatusRepositoryResponse> {
    const { data } = await this.client.query<IDisputeTrackingStatusQueryResponse>({
      query: GET_DISPUTE_TRACKING_STATUSES,
    });

    if (!data) {
      throw new Error("Failed to fetch dispute tracking statuses");
    }

    const disputeTrackingStatusesResponse = data.disputeTrackingStatuses;

    return {
      disputeTrackingStatuses: disputeTrackingStatusesResponse.items.map(
        status => new DisputeTrackingStatus(status)
      ),
      count: disputeTrackingStatusesResponse.count,
    };
  }

  // Products
  async getProducts(params?: IProductLookUpRequest): Promise<IProductLookUpRepositoryResponse> {
    const { data } = await this.client.query<IProductLookUpQueryResponse>({
      query: GET_PRODUCTS,
      variables: {
        params: params ?? {},
      },
    });

    if (!data) {
      throw new Error("Failed to fetch products");
    }

    const productsResponse = data.products;

    return {
      products: productsResponse.items.map(product => new ProductLookUp(product)),
      count: productsResponse.total,
      page: productsResponse.page,
      limit: productsResponse.limit,
    };
  }
}
